import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "react-toastify";
import { getUserRole } from "../../utils/getUserRole";

import SearchBar from "../../components/SearchBar";
import LoadingOverlay from "../../components/LoadingOverlay";
import Pagination from "../../components/Pagination";

function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClientName, setSelectedClientName] = useState("");
  
  const isAdmin = getUserRole() === "Administrador";

  const ITEMS_PER_PAGE = 30;
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    if (isAdmin) {
      fetchClients();
    } else {
      fetchInventory();
    }
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const res = await api.get("/clients");
      setClients(res.data || []);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar los clientes.");
      setLoading(false);
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchInventory = async (clientId = null) => {
    try {
      setLoading(true);

      let url = "/client-inventory-stock";
      if (clientId) {
        url += `/${clientId}`;
      }

      const res = await api.get(url);
      setInventory(res.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar el inventario.");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadInventory = () => {
    if (!selectedClientId) {
      toast.warning("Por favor selecciona un cliente.");
      return;
    }
    const client = clients.find(c => c.id == selectedClientId);
    setSelectedClientName(client?.name || "");
    fetchInventory(selectedClientId);
  };

  const filteredInventory = inventory.filter((item) =>
    `${item.product?.code || ""} ${item.product?.name || ""}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const pageCount = Math.ceil(filteredInventory.length / ITEMS_PER_PAGE);

  const currentItems = filteredInventory.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  if (isAdmin) {
    return (
      <div className="relative rounded-xl bg-white p-4 shadow-md sm:p-6">
        <LoadingOverlay loading={loadingClients} />

        <div className="mb-6">
          <h2 className="mb-4 text-2xl font-semibold text-gray-800 sm:text-3xl">
            Seleccionar Bodega
          </h2>

          <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-end">
            <div className="w-full min-w-0 sm:flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cliente / Área
              </label>
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Selecciona un cliente --</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleLoadInventory}
              disabled={loading}
              className="w-full rounded-lg bg-blue-600 px-6 py-2 text-white shadow transition duration-300 hover:bg-blue-700 disabled:bg-gray-400 sm:w-auto"
            >
              {loading ? "Cargando..." : "Bodega"}
            </button>
          </div>
        </div>

        {inventory.length > 0 && (
          <>
            <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
              <h2 className="text-xl font-semibold text-gray-800 sm:text-2xl">
                {selectedClientName}
              </h2>
              <div className="w-full sm:w-64">
                <SearchBar
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl shadow">
              <table className="min-w-[520px] bg-white table-auto">
                <thead>
                  <tr className="bg-gray-100 text-gray-700 text-center text-sm uppercase tracking-wider">
                    <th className="px-3 py-3 sm:px-6">Producto</th>
                    <th className="px-3 py-3 sm:px-6">Stock disponible</th>
                  </tr>
                </thead>

                <tbody className="text-center">
                  {currentItems.length === 0 ? (
                    <tr>
                      <td
                        colSpan="2"
                        className="px-3 py-6 text-center italic text-gray-400 sm:px-6"
                      >
                        No hay productos en la bodega.
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((item, index) => (
                      <tr
                        key={item.id}
                        className={`border-t ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                      >
                        <td className="px-3 py-4 sm:px-6">
                          {item.product?.name}
                        </td>

                        <td className="px-3 py-4 sm:px-6">
                          <span className="font-semibold text-blue-700">
                            {item.quantity}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              <Pagination
                pageCount={pageCount}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                currentItemsCount={currentItems.length}
                totalItemsCount={filteredInventory.length}
              />
            </div>
          </>
        )}
      </div>
    );
  }

  // Vista normal para usuarios no administradores
  return (
    <div className="relative rounded-xl bg-white p-4 shadow-md sm:p-6">
      <LoadingOverlay loading={loading} />

      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <h2 className="text-2xl font-semibold text-gray-800 sm:text-3xl">
          Mi Bodega
        </h2>

        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
          <div className="w-full sm:w-64">
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <Link
            to="/my-inventory/exit"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow transition duration-300 hover:bg-blue-700 sm:w-auto"
          >
            <Plus size={18} />
            Registrar salida
          </Link>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-[520px] bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center text-sm uppercase tracking-wider">

              <th className="px-3 py-3 sm:px-6">Producto</th>

              <th className="px-3 py-3 sm:px-6">Stock disponible</th>
            </tr>
          </thead>

          <tbody className="text-center">
            {currentItems.length === 0 ? (
              <tr>
                <td
                  colSpan="3"
                  className="px-3 py-6 text-center italic text-gray-400 sm:px-6"
                >
                  No hay productos en la bodega.
                </td>
              </tr>
            ) : (
              currentItems.map((item, index) => (
                <tr
                  key={item.id}
                  className={`border-t ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >

                  <td className="px-3 py-4 sm:px-6">
                    {item.product?.name}
                  </td>

                  <td className="px-3 py-4 sm:px-6">
                    <span className="font-semibold text-blue-700">
                      {item.quantity}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <Pagination
          pageCount={pageCount}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          currentItemsCount={currentItems.length}
          totalItemsCount={filteredInventory.length}
        />
      </div>
    </div>
  );
}

export default Inventory;